/**
 * @fileOverview منطق تحليل بيانات السوق وحساب النصائح الذكية للمشترين.
 */

import { Listing, MOCK_LISTINGS } from '@/app/lib/mock-db';

export interface MarketStats {
  avgPrice: number;
  minPrice: number;
  maxPrice: number;
  count: number;
}

/**
 * جلب إحصائيات السوق للسيارات المشابهة (نفس الماركة، الموديل، الدولة، وسنة قريبة).
 */
export function getSimilarMarketStats(listing: Listing): MarketStats {
  const similar = MOCK_LISTINGS.filter(l => 
    l.make_id === listing.make_id &&
    l.model_id === listing.model_id &&
    Math.abs(l.year - listing.year) <= 1
  );

  if (similar.length === 0) {
    return { avgPrice: listing.price, minPrice: listing.price, maxPrice: listing.price, count: 0 };
  }

  const prices = similar.map(l => l.price);
  const sum = prices.reduce((a, b) => a + b, 0);
  
  return {
    avgPrice: Math.round(sum / similar.length),
    minPrice: Math.min(...prices),
    maxPrice: Math.max(...prices),
    count: similar.length
  };
}

/**
 * حساب درجة الصفقة من 1 إلى 10.
 */
export function computeDealScore(price: number, avgPrice: number) {
  const delta = ((price - avgPrice) / avgPrice) * 100;
  let score = 5;
  let message = "";

  if (delta <= -10) { score = 9; message = "سعر ممتاز! أقل من متوسط السوق بنسبة " + Math.abs(Math.round(delta)) + "%"; }
  else if (delta <= -3) { score = 8; message = "صفقة جيدة. السعر أقل من المتوسط."; }
  else if (delta <= 3) { score = 6; message = "سعر عادل ومتوافق مع السوق."; }
  else if (delta <= 12) { score = 4; message = "السعر مرتفع قليلاً عن المتوسط."; }
  else { score = 2; message = "السعر أعلى من متوسط السوق بشكل ملحوظ."; }

  return { score, deltaPercent: Math.round(delta), message };
}

/**
 * تحديد قوة التفاوض المقترحة.
 */
export function computeNegotiation(price: number, avgPrice: number, createdAt: string) {
  const daysOld = Math.floor((Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60 * 24));
  const isOverpriced = price > avgPrice * 1.05;

  if (isOverpriced || daysOld > 14) {
    return { level: "قوي", suggestion: "جرّب تقديم عرض أقل بـ 5%–10%، السيارة معروضة منذ فترة أو سعرها مرتفع." };
  }
  if (daysOld > 7) {
    return { level: "متوسط", suggestion: "هناك مجال بسيط للتفاوض، اطلب خصماً رمزياً." };
  }
  return { level: "ضعيف", suggestion: "السيارة جديدة في السوق، التفاوض قد يكون صعباً حالياً." };
}

/**
 * تقديم نصيحة بناءً على نوع الاستخدام والمدخلات التفاعلية.
 */
export function computeFitAdvice(listing: Listing, purpose: string, distance: string, priority: string) {
  let score = 0;
  let strengths = [];
  let warnings = [];

  // منطق تحليل الغرض
  if (purpose === 'city') {
    score += 80;
    strengths.push("سهولة التنقل والاصطفاف");
  } else if (purpose === 'travel') {
    if (listing.year < 2018) warnings.push("قد تحتاج لفحص مكثف للأنظمة قبل الرحلات الطويلة");
    score += 60;
  }

  // منطق المسافة
  if (distance === 'long' && priority === 'savings') {
    warnings.push("المسافات الطويلة مع هذا المحرك قد تزيد من ميزانية الوقود");
  }

  // توليد الرسالة النهائية
  let finalMessage = "";
  if (priority === 'savings') finalMessage = "هذا الخيار اقتصادي جداً لميزانيتك.";
  else if (priority === 'comfort') finalMessage = "توفر هذه السيارة راحة مقبولة لفئتها.";
  else finalMessage = "الأداء هو الميزة الأقوى هنا.";

  return {
    status: score > 70 ? "ممتاز" : "جيد",
    message: finalMessage,
    strengths,
    warnings
  };
}